/**
 * A plat sheet: parcel outlines, street names, house numbers. Black on white.
 *
 * The existing WardMap answers "roughly where is this household". This answers
 * the question somebody standing on a sidewalk has — which house is that, and
 * what is the next one — so it is drawn the way a county plat is drawn: thin
 * black lines, white fill, no colour, no basemap tiles.
 *
 * Deliberately inline SVG over static geometry rather than a tile map. It
 * needs no network at the command post and has no dependency on a map provider
 * staying up during an incident.
 *
 * This is the screen renderer. What to draw is decided in plat/model.ts, which
 * the PDF generator renders too — so the preview here and the sheet that comes
 * out of the printer are the same map, drawn twice.
 */
import { useEffect, useMemo } from 'react';

import { BOUNDARY } from './WardMap';
import {
  LOT_LINE,
  buildPlat, segPath, HATCH_LINE, HATCH_OPACITY, HATCH_PX,
  MIN_LABEL_PX, OUTSIDE_GRAY, STREET_LABEL_PX,
  type PlatData, type PlatFocus, type PlatHousehold,
} from '../plat/model';

export type { PlatData, PlatFocus, PlatHousehold, Parcel, Road } from '../plat/model';

export default function PlatMap({
  data, households, focus, limitTo, title, subtitle, orientation = 'auto',
  onOrientation, showShading = true, showNames = true,
}: {
  data: PlatData;
  /** Every household in the ward, not just the ones this sheet is about. */
  households: PlatHousehold[];
  focus: PlatFocus;
  /** Household ids the sheet is limited to — a search or report. */
  limitTo?: ReadonlySet<string>;
  title: string;
  subtitle?: string;
  /**
   * 'auto' turns the sheet to match the sector. Several sectors are a band
   * along one street — three times wider than tall — and forcing those into
   * portrait wastes half the page and shrinks the house numbers to nothing.
   */
  orientation?: 'auto' | 'portrait' | 'landscape';
  onOrientation?: (o: 'portrait' | 'landscape') => void;
  /** Grey fill on the households this sheet is about. */
  showShading?: boolean;
  /** Surnames and property names under the house numbers. */
  showNames?: boolean;
  /**
   * The note listing households that fall outside the ward. Off on the ward
   * sheet, where it is a page of names rather than a footnote.
   */
  /**
   * Households drawn as a dot because no county parcel matched their address,
   * and the footnote counting them.
   */
}) {
  const model = useMemo(
    () => buildPlat({ data, households, focus, limitTo, orientation, showNames }),
    [data, households, focus, limitTo, orientation, showNames],
  );

  const { frame, unitsPerPx } = model;
  /** A PDF-style 0..1 grey as a CSS colour, so both renderers share one number. */
  const gray = (g: number) => {
    const v = Math.round(g * 255);
    return `rgb(${v},${v},${v})`;
  };
  const stroke = (px: number) => px * unitsPerPx;

  /** Lot lines for one side of the sector divide; see the draw order below. */
  const lots = (mine: boolean) => model.parcels
    .filter((p) => p.inSector === mine)
    .map((p, i) => (
      <g key={`${mine ? 'i' : 'o'}${i}`}>
        {p.rings.map((r, j) => (
          <polygon
            key={j}
            points={r.map((pt) => `${pt.x},${pt.y}`).join(' ')}
            fill={showShading && p.filled ? '#d9d9d9' : '#ffffff'}
            // A grouped parcel is filled but not outlined here; its
            // property's merged outline is drawn over the top.
            stroke={p.outlined ? (mine ? '#000000' : gray(OUTSIDE_GRAY)) : 'none'}
            // One weight for every lot line, whether a member lives on it
            // or not. The shading is what says that, and a second signal
            // saying the same thing only made the lot lines look ragged.
            strokeWidth={stroke(LOT_LINE)}
          />
        ))}
      </g>
    ));

  /** Merged property outlines for one side of the divide. */
  const properties = (mine: boolean) => model.merged
    .filter((m) => m.inSector === mine)
    .map((m, i) => (
      <path
        key={`m${mine ? 'i' : 'o'}${i}`}
        d={segPath(m.d)}
        fill="none"
        stroke={mine ? '#000000' : gray(OUTSIDE_GRAY)}
        strokeWidth={stroke(LOT_LINE)}
        strokeLinecap="square"
      />
    ));

  useEffect(() => {
    onOrientation?.(model.landscape ? 'landscape' : 'portrait');
  }, [model.landscape, onOrientation]);

  return (
    <figure
      className={`plat m-0 ${model.landscape ? 'plat--landscape' : ''}`}
      style={{
        '--plat-w': `${model.paper.w.toFixed(2)}in`,
        '--plat-h': `${model.paper.h.toFixed(2)}in`,
      } as React.CSSProperties}
    >
      <figcaption className="mb-1 flex items-baseline justify-between gap-3">
        <span className="text-[11pt] font-semibold text-black">{title}</span>
        {subtitle && <span className="text-[8pt] text-slate-600">{subtitle}</span>}
      </figcaption>

      <svg
        viewBox={`${frame.x0} ${frame.y0} ${frame.x1 - frame.x0} ${frame.y1 - frame.y0}`}
        className="block w-full border border-black bg-white"
        role="img"
        aria-label={`Plat map of ${title}`}
      >
        <defs>
          {/* Phased in user space, not per shape, so the stripes run straight
              across a property drawn as many polygons instead of restarting
              at every lot line. */}
          <pattern
            id="plat-mu"
            patternUnits="userSpaceOnUse"
            width={stroke(HATCH_PX)}
            height={stroke(HATCH_PX)}
            patternTransform="rotate(45)"
          >
            <line
              x1={0}
              y1={0}
              x2={0}
              y2={stroke(HATCH_PX)}
              stroke="#000000"
              strokeWidth={stroke(HATCH_LINE)}
              opacity={HATCH_OPACITY}
            />
          </pattern>
        </defs>

        {/* Every parcel, so a walker can see the lots either side of theirs. */}
        {/* Outside the sector first — ALL of it, lot lines and merged
            property outlines alike — so a sector lot's own line is never
            overdrawn by a paler neighbour sharing the same edge. A grouped
            property has no lot lines, so its merged outline is its only edge;
            leaving every merged outline until last greyed out whatever
            stretch a sector lot shared with one. See sheet.ts. */}
        {lots(false)}
        {properties(false)}
        {lots(true)}

        {/* Multi-unit buildings, hatched over the fill and under the lot
            lines. A separate pass rather than a fill on the polygon above,
            because the stripes have to sit on top of the grey where a lot is
            both ours and multi-unit. */}
        {model.parcels.filter((p) => p.striped).map((p, i) => (
          <g key={`s${i}`}>
            {p.rings.map((r, j) => (
              <polygon
                key={j}
                points={r.map((pt) => `${pt.x},${pt.y}`).join(' ')}
                fill="url(#plat-mu)"
                stroke="none"
                // Fades with its lot; see the PDF renderer for why.
                opacity={p.inSector ? 1 : 0.35}
              />
            ))}
          </g>
        ))}

        {/* The in-sector half, after the hatch like the lot lines above it. */}
        {properties(true)}

        {model.roads.map((r, i) => (
          <polyline
            key={i}
            points={r.pts.map((p) => `${p.x},${p.y}`).join(' ')}
            fill="none"
            stroke="#000000"
            strokeWidth={stroke(0.4)}
            strokeDasharray={`${stroke(3)} ${stroke(3)}`}
            opacity={0.35}
          />
        ))}

        {/* One name per street, turned to follow it but never upside down. */}
        {model.streetLabels.map((l) => (
          <text
            key={l.name}
            x={l.x}
            y={l.y}
            fontSize={STREET_LABEL_PX * unitsPerPx}
            textAnchor="middle"
            fill="#000000"
            transform={`rotate(${l.deg.toFixed(1)} ${l.x} ${l.y})`}
            paintOrder="stroke"
            stroke="#ffffff"
            strokeWidth={stroke(1.6)}
            strokeLinejoin="round"
          >
            {l.name}
          </text>
        ))}

        {/* The ward edge, heavy enough to read at arm's length. Drawn after
            the parcels because it now mostly *coincides* with lot lines rather
            than crossing them, and the heavy line has to be the one you see. */}
        <polygon
          points={BOUNDARY.map(([e, so]) => `${e},${so}`).join(' ')}
          fill="none"
          stroke="#000000"
          strokeWidth={stroke(3)}
          strokeLinejoin="round"
        />

        {/* No dots. See sheet.ts: a geocode on a named street is interpolated
            along the block, so the mark looked as placed as a shaded lot and
            was not. The households are listed under the map instead. */}

        {model.labels.map((l, i) => (
          <text
            key={i}
            x={l.x}
            y={l.y}
            fontSize={MIN_LABEL_PX * unitsPerPx}
            textAnchor="middle"
            fill="#000000"
            fontWeight={l.bold ? 700 : 400}
            {...(l.halo ? {
              paintOrder: 'stroke' as const,
              stroke: '#ffffff',
              strokeWidth: stroke(1.2),
              strokeLinejoin: 'round' as const,
            } : {})}
          >
            {l.text}
          </text>
        ))}
      </svg>

      {model.listed.length > 0 && (
        <ol className="mt-1 grid grid-cols-3 gap-x-4 text-[7pt] leading-tight text-black">
          {model.listed.map((l) => (
            <li key={l.n}>
              <span className="font-semibold">{l.n}</span> {l.address}
            </li>
          ))}
        </ol>
      )}
      {/* What the drawing cannot show. On the printed sheet this is a page of
          the roster; on screen there is no roster, so it sits under the map
          rather than being lost. Same two groups, same order. */}
      {(model.orphans.length > 0 || model.outside.length > 0) && (
        <div className="mt-1 border-t border-black pt-1 text-[7.5pt] leading-tight text-black">
          <span className="font-semibold">Not on the map</span>
          {model.orphans.length > 0 && (
            <>
              <p className="mt-0.5 text-slate-600">
                In the ward — no county parcel matches the address
              </p>
              <ul className="grid grid-cols-2 gap-x-4 sm:grid-cols-3">
                {model.orphans.map((h) => (
                  <li key={`o${h.key}`}>
                    {h.label ? `${h.label} — ` : ''}{h.unit ? `${h.key} ${h.unit}` : h.key}
                  </li>
                ))}
              </ul>
            </>
          )}
          {model.outside.length > 0 && (
            <>
              <p className="mt-0.5 text-slate-600">Outside the ward boundary</p>
              <ul className="grid grid-cols-2 gap-x-4 sm:grid-cols-3">
                {model.outside.map((h) => (
                  <li key={`x${h.key}`}>
                    {h.label ? `${h.label} — ` : ''}{h.unit ? `${h.key} ${h.unit}` : h.key}
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      )}
    </figure>
  );
}
